/**
 * ProjectKnowledgeBaseEditor component for editing project-level context items
 * Used on project create/edit forms to manage knowledge base items
 */
import * as React from 'react'
import { Plus, X, AlertCircle, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { PROJECT_CONTEXT_ITEM_TYPES } from '@/types/chatbot'
import type { ProjectContextItemInput } from '@/lib/types'
import { ContextItemTypeIcon, getTypeLabel } from '@/components/chatbot/ContextItemTypeIcon'

export interface ProjectKnowledgeBaseEditorProps {
  /** Current list of context items */
  items: ProjectContextItemInput[]
  /** Callback when items change */
  onChange: (items: ProjectContextItemInput[]) => void
  /** Maximum number of items allowed */
  maxItems?: number
  /** Whether the editor is disabled */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Available types for project knowledge base items
 */
const availableTypes = [
  { value: PROJECT_CONTEXT_ITEM_TYPES.WIKI_ADR, label: 'ADR (Decision Record)' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.WIKI_ARCH, label: 'Architecture Doc' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.WIKI_GUIDE, label: 'Guide / How-to' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.WIKI_RUNBOOK, label: 'Runbook' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.EPIC, label: 'Epic' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.FEATURE, label: 'Feature' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.ADO_WORKITEM, label: 'Work Item' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.DOC_API, label: 'API Documentation' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.DOC_DESIGN, label: 'Design Document' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.DOC_EXTERNAL, label: 'External Link' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.WIKI_PAGE, label: 'Wiki Page' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.FILE, label: 'File' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.URL, label: 'URL' },
  { value: PROJECT_CONTEXT_ITEM_TYPES.NOTE, label: 'Note' },
] as const

/**
 * Editor component for managing project-level context items (knowledge base).
 * Supports adding items with type, value, and optional note.
 *
 * @example
 * ```tsx
 * const [items, setItems] = useState<ProjectContextItemInput[]>([])
 *
 * <ProjectKnowledgeBaseEditor
 *   items={items}
 *   onChange={setItems}
 *   maxItems={50}
 * />
 * ```
 */
export function ProjectKnowledgeBaseEditor({
  items,
  onChange,
  maxItems = 50,
  disabled = false,
  className
}: ProjectKnowledgeBaseEditorProps) {
  const [selectedType, setSelectedType] = React.useState<string>(PROJECT_CONTEXT_ITEM_TYPES.WIKI_ADR)
  const [valueInput, setValueInput] = React.useState('')
  const [noteInput, setNoteInput] = React.useState('')

  const canAdd = items.length < maxItems && !disabled
  const isMaxReached = items.length >= maxItems

  const handleAdd = () => {
    const trimmedValue = valueInput.trim()
    if (!trimmedValue || !canAdd) return

    const newItem: ProjectContextItemInput = {
      type: selectedType,
      value: trimmedValue,
      ...(noteInput.trim() && { note: noteInput.trim() })
    }

    onChange([...items, newItem])
    setValueInput('')
    setNoteInput('')
  }

  const handleRemove = (index: number) => {
    const newItems = items.filter((_, i) => i !== index)
    onChange(newItems)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Add item form */}
      <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>Add Knowledge Base Item</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Knowledge base items provide context to the AI for all sessions in this project. Add ADRs, architecture docs, epics, or other relevant references.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        <div className="flex gap-2">
          <Select
            value={selectedType}
            onValueChange={setSelectedType}
            disabled={disabled || isMaxReached}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {availableTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center gap-2">
                    <ContextItemTypeIcon type={type.value} size="sm" />
                    <span>{type.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Input
            value={valueInput}
            onChange={(e) => setValueInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Value (ID, path, URL...)"
            disabled={disabled || isMaxReached}
            className="flex-1"
          />
        </div>
        
        <div className="flex gap-2">
          <Input
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Note (optional description)"
            disabled={disabled || isMaxReached}
            className="flex-1"
          />
          
          <Button
            type="button"
            variant="outline"
            onClick={handleAdd}
            disabled={!canAdd || !valueInput.trim()}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Count indicator */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{items.length} of {maxItems} items</span>
        {isMaxReached && (
          <span className="flex items-center gap-1 text-amber-600">
            <AlertCircle className="h-3 w-3" />
            Maximum reached
          </span>
        )}
      </div>

      {/* Items list */}
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center border rounded-md bg-muted/30">
          No knowledge base items added yet. Add items to provide project context to the AI.
        </p>
      ) : (
        <ul className="space-y-2" role="list">
          {items.map((item, index) => (
            <li
              key={index}
              className="flex items-start gap-3 p-3 rounded-md bg-muted/50 border"
              role="listitem"
            >
              <ContextItemTypeIcon type={item.type} size="md" className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase">
                    {getTypeLabel(item.type)}
                  </span>
                </div>
                <p className="text-sm font-medium break-all">{item.value}</p>
                {item.note && (
                  <p className="text-xs text-muted-foreground mt-1">{item.note}</p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => handleRemove(index)}
                disabled={disabled}
                aria-label={`Remove item: ${item.value}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
